import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import classNames from 'classnames'
import * as React from 'react'
import type {ReactNode} from 'react'

import {BuildLineLayoutContext} from '../../../contexts/buildLineLayout'
import {useOneLine} from '../../../contexts/oneLine'
import {useSetQueryParam} from '../../../hooks/routes'
import type {NormalizedBuild, ProjectOrBuildTypeNode} from '../../../types'
import {noop} from '../../../utils/empty'
import Grid from '../Grid/Grid'
import RowGroupsWrapper from '../Grid/RowGroupsWrapper'
import Loader from '../Loader/Loader'

import BuildDetailsMargin from './BuildDetailsMargin'
import BuildDetailsRender from './BuildDetailsRender'
import type {BuildsProps} from './Builds.types'
import BuildsHeader from './BuildsHeader/BuildsHeader'
import QueuedToggler from './QueuedToggler/QueuedToggler'

import styles from './Builds.css'

type RenderedLinesProps = {
  alwaysShowLineBorders: boolean
  builds: ReadonlyArray<NormalizedBuild>
  wrapped: boolean
  filterQueued: boolean
  showMargin: boolean
  pathFilter?: ProjectOrBuildTypeNode | null
  renderBuild?: (build: NormalizedBuild, prevBuildId?: number, nextBuildId?: number) => ReactNode
}
const RenderedLines = React.memo(function RenderedLines({
  filterQueued,
  showMargin,
  builds,
  ...restProps
}: RenderedLinesProps) {
  if (filterQueued) {
    return (
      <React.Fragment>
        {builds
          .filter(build => build.state === 'queued')
          .map((build, index) => (
            <BuildDetailsRender
              key={build.id}
              builds={builds}
              build={build}
              index={index}
              {...restProps}
            />
          ))}
        {showMargin && <BuildDetailsMargin />}
        {builds
          .filter(build => build.state !== 'queued')
          .map((build, index) => (
            <BuildDetailsRender
              key={build.id}
              builds={builds}
              build={build}
              index={index}
              {...restProps}
            />
          ))}
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      {showMargin && <BuildDetailsMargin />}
      {builds.map((build, index) => (
        <BuildDetailsRender
          key={build.id}
          builds={builds}
          build={build}
          index={index}
          {...restProps}
        />
      ))}
    </React.Fragment>
  )
})
RenderedLines.displayName = 'RenderedLines'
const loader = <Loader className={styles.loader} />

export function useBuildsClassName(): string {
  const oneLine = useOneLine()
  const {showPath, showBranch, showAgent, showDuration, showTags} =
    React.useContext(BuildLineLayoutContext)
  return classNames(styles.builds, {
    [styles.withPath]: showPath,
    [styles.withBranch]: showBranch,
    [styles.withAgent]: showAgent,
    [styles.withDuration]: showDuration,
    [styles.withTags]: showTags,
    [styles.oneLine]: oneLine,
  })
}

function Builds({
  toggleQueuedVisibility = noop,
  alwaysShowLineBorders = false,
  showQueuedBuilds,
  builds = [],
  inlineLoader,
  placeholder = 'No builds available',
  ready = false,
  show,
  disableProgressiveLoading,
  withLoader,
  withTitle = false,
  showOverlay = false,
  stats,
  showQueuedToggler = false,
  buildLineLayout: {showAgent, showExactDuration, showPath, showBranch, showDuration} = {},
  wrapped = false,
  buildTypeId,
  branch,
  hasParentGrid = false,
  pathFilter,
  renderBuild,
}: BuildsProps) {
  const oneLine = useOneLine()
  const classes = classNames({
    [styles.hasParentGrid]: hasParentGrid,
    [useBuildsClassName()]: !hasParentGrid,
    [styles.withOverlay]: showOverlay,
    [styles.wrappedContainer]: wrapped,
  })
  const setPage = useSetQueryParam('page', '1')

  if (!show || (disableProgressiveLoading === true && !ready)) {
    if (withLoader === true) {
      return inlineLoader === true ? (
        <span className={styles.loader}>
          <LoaderInline />
          {' Loading...'}
        </span>
      ) : (
        loader
      )
    } else {
      return null
    }
  } else if (!builds.length && !showQueuedToggler && placeholder != null) {
    return <div className={styles.placeholder}>{placeholder}</div>
  }

  const showMargin =
    wrapped &&
    oneLine === true &&
    !(showQueuedToggler && builds.every(build => build.state === 'queued'))
  const children = (
    <>
      {withTitle && (
        <BuildsHeader
          withPath={showPath}
          withBranch={showBranch}
          withAgent={showAgent}
          withDuration={showDuration}
          showExactDuration={showExactDuration}
        />
      )}
      <RowGroupsWrapper
        className={classNames({
          [styles.wrapped]: wrapped,
        })}
      >
        {showQueuedToggler && (
          <QueuedToggler
            show={showQueuedBuilds === true}
            count={stats?.queued ?? 0}
            onToggle={() => {
              setPage('1')
              toggleQueuedVisibility(buildTypeId, branch)
            }}
            buildTypeId={buildTypeId}
            containerClassName={styles.buttonContainer}
            buttonClassName={styles.showQueuedToggle}
          />
        )}
        <RenderedLines
          alwaysShowLineBorders={alwaysShowLineBorders}
          builds={builds}
          wrapped={wrapped}
          filterQueued={showQueuedToggler}
          showMargin={showMargin}
          pathFilter={pathFilter}
          renderBuild={renderBuild}
        />
        {showMargin && <BuildDetailsMargin />}
      </RowGroupsWrapper>
      {!ready && (
        <div className={styles.inlineLoader}>
          <LoaderInline>{'Loading...'}</LoaderInline>
        </div>
      )}
    </>
  )
  return hasParentGrid ? (
    <div className={classes}>{children}</div>
  ) : (
    <Grid className={classes}>{children}</Grid>
  )
}

export default React.memo(Builds)
